import { useQueryParamKey } from '@cobliteam/hooks';
import { useTranslation } from '@cobliteam/i18n';
import { Popover, Typography } from '@cobliteam/ui/components';
import SelectTimeWindow from '@cobliteam/ui-business/components/Selects/SelectTimeWindow/SelectTimeWindow';
import { format } from '@cobliteam/utils/date';
import { memo, useCallback, useEffect, useRef, useState } from 'react';

interface TrackingTimeFilterProps {
  tripMaxTime: number;
  tripMinTime: number;
  onTimeChange: (tripStartTime: number, tripEndTime: number) => void;
}

export const TrackingTimeFilter = ({ tripMaxTime, tripMinTime, onTimeChange }: TrackingTimeFilterProps) => {
  const { t } = useTranslation('tracking');
  const { paramKey: eventTime } = useQueryParamKey('event_time');
  const [tripMinTimeState, setTripMinTime] = useState<number | undefined>(tripMinTime);
  const [tripMaxTimeState, setTripMaxTime] = useState<number | undefined>(tripMaxTime);
  const processedEventTimeRef = useRef<string | null>(null);

  useEffect(() => {
    if (eventTime && processedEventTimeRef.current !== eventTime) {
      const start = Number(eventTime) - 1 * 60 * 1000; // 10 minutes before
      const end = Number(eventTime) + 1 * 60 * 1000; // 10 minutes after
      setTripMinTime(start);
      setTripMaxTime(end);
      onTimeChange(start, end);
      processedEventTimeRef.current = eventTime as string | null;
    }
  }, [eventTime, onTimeChange]);

  // Update state when props change (but not when eventTime is being processed)
  useEffect(() => {
    if (!eventTime || processedEventTimeRef.current !== eventTime) {
      setTripMinTime(tripMinTime);
      setTripMaxTime(tripMaxTime);
    }
  }, [tripMinTime, tripMaxTime, eventTime]);

  // prevent loops
  const handleTimeChange = useCallback(
    (start: number, end: number) => {
      if (start !== tripMinTimeState || end !== tripMaxTimeState) {
        setTripMinTime(start);
        setTripMaxTime(end);
        onTimeChange(start, end);
      }
    },
    [tripMinTimeState, tripMaxTimeState, onTimeChange]
  );

  return (
    <div className='mb-5 flex flex-col'>
      <div className='flex items-center'>
        <div className='flex items-center gap-1'>
          <Typography variant='text-md-semibold'>{t('route_time')}:</Typography>
          <Typography variant='text-md' className='font-normal'>
            {format(tripMinTime, 'HH:mm')} - {format(tripMaxTime, 'HH:mm')}
          </Typography>
        </div>
        <Popover>
          <Popover.Trigger>
            <Popover.Icon />
          </Popover.Trigger>
          <Popover.Content className='w-72'>
            <Typography variant='body'>
              {t('time_filter_will_be_applied')} <strong>{t('only_on_map')}</strong>. {t('to_analyze_route_sections_data_consult_report')}
            </Typography>
          </Popover.Content>
        </Popover>
      </div>

      <SelectTimeWindow
        size='sm'
        minStartTime={tripMinTime}
        maxEndTime={tripMaxTime}
        //selectedTimeWindow={tripMinTimeState && tripMaxTimeState ? [tripMinTimeState, tripMaxTimeState] : undefined}
        onTimeChange={handleTimeChange}
        showPresets={true}
      />
    </div>
  );
};

export default memo(TrackingTimeFilter);
